// Minimal GraphQL fetch client. Stays in-house for the skeleton — the surface
// is too small to justify pulling in urql / Apollo / graphql-request yet.
//
// Endpoint:
//   - dev (vite serve): hits '/api/graphql', proxied by vite.config.ts to the
//     deployed anvil-api-dev Worker (avoids CORS in local dev).
//   - prod (vite build → Pages): hits same-origin '/api/graphql' if API and UI
//     share a domain, otherwise set VITE_API_BASE_URL to point at the API.

const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? '';

// REST callers (file uploads, webhooks) need the same base URL as GraphQL.
export const API_BASE = API_BASE_URL;

interface GraphqlResponse<T> {
  readonly data?: T;
  readonly errors?: ReadonlyArray<{ message: string; path?: ReadonlyArray<string | number> }>;
}

export class GraphqlError extends Error {
  constructor(
    message: string,
    public readonly errors?: ReadonlyArray<{ message: string }>,
  ) {
    super(message);
    this.name = 'GraphqlError';
  }
}

export async function graphql<T = unknown>(
  query: string,
  variables?: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api/graphql`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ query, variables: variables ?? {} }),
  });
  if (!res.ok) {
    throw new GraphqlError(`GraphQL HTTP ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as GraphqlResponse<T>;
  if (body.errors && body.errors.length > 0) {
    throw new GraphqlError(
      body.errors.map((e) => e.message).join('; '),
      body.errors,
    );
  }
  if (body.data === undefined) {
    throw new GraphqlError('GraphQL response missing data');
  }
  return body.data;
}
